<template>
  <div class="gmp-modal">
    <div class="my-account__modal-box no-scroll-modal my-account__modal-box--usage-detail modal-evcharging-settings">
      <div class="modal-usage-pick-a-view__header">
        <span class="modal-usage-pick-a-view__title modal-title">Settings</span>
        <button @click="closeModal()" type="button" class="close bill-pay__close gds-button gds-button-circle gds-secondary">
          <svg class="gds-icon close-icon">
            <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-close-small" />
          </svg>
        </button>
      </div>
      <FocusLock>
        <div class="modal-usage-pick-a-view__modal-body">
          <div v-if="loadState ==='loading'" class="table-loading" />
          <div
          class="settings-modal"
          v-if="loadState ==='complete' && evSettingsForEdit">
            <div class="your-ev-information-section">
              <h2 class="section-title">Your EV Information</h2>
              <div class="form-row">
                <div class="form-group ev-model">
                  <label for="ev-model">Make / Model</label>
                  <div class="form-control select-container">
                    <label>
                      <select
                        class="modal-evcharging-settings__select"
                        @change="onMakeModelChange"
                        v-model="evSettingsForEdit.selectedMakeModel">
                          <option value="">Pick vehicle make/model</option>
                          <option
                          v-for="model in evSettingsForEdit.makeModels" :key="model.name" :value="model.name">
                          {{ model.name }}
                          </option>
                          <option :value="CUSTOM_VEHICLE_OPTION">Other/Custom</option>
                      </select>
                      <span class="modal-evcharging-settings__custom-caret">&#9662;</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <!-- Custom Vehicle Name Input (only shown when Other/Custom is selected) -->
              <div class="form-row gds-flex-container gds-flex-container--bottom" v-if="isCustomVehicleMode">
                <div class="form-group">
                  <label for="vehicle-name">Vehicle Name</label>
                  <input
                    type="text"
                    class="form-control"
                    placeholder=""
                    maxlength="40"
                    v-model="customVehicleName"
                    @input="checkValidation"
                    :class="{ 'ev-charger-settings-validation-error': validationErrors.vehicleName}"/>
                </div>
              </div>

              <!-- Energy Consumption -->
              <div class="form-row gds-flex-container gds-flex-container--bottom">
                <div class="form-group energy-consumption-group">
                  <div v-if="shouldShowEnergyConsumptionDisplay" 
                       class="energy-consumption-display gds-flex-container gds-flex-container--left">
                    <span class="energy-label">Energy Consumption:</span>
                    <span class="energy-value">{{ evSettingsForEdit.mpgEquivalent }} MPGe</span>
                    <button type="button" class="edit-button" @click="startEditingEnergyConsumption">Edit</button>
                  </div>
                  <div v-else-if="shouldShowEnergyConsumptionPlaceholder" class="energy-consumption-display">
                    <span class="energy-label">Energy Consumption:</span>
                    <span class="energy-value-placeholder">--</span>
                  </div>
                  <div v-else class="energy-consumption-input-container">
                    <label>Energy Consumption</label>
                    <div class="input-row gds-flex-container gds-flex-container--left">
                      <input
                        type="text"
                        class="form-control energy-input"
                        v-model="energyConsumptionValue"
                        @input="onEnergyConsumptionInput"
                        @keyup="checkValidation()"
                        :class="{ 'ev-charger-settings-validation-error': validationErrors.mpgEquivalent}"/>
                      <div class="form-control select-container unit-selector">
                        <label>
                          <select
                            class="modal-evcharging-settings__select"
                            v-model="energyConsumptionUnit"
                            @change="onUnitChange">
                              <option :value="MPGE">{{ MPGE }}</option>
                              <option :value="MILES_PER_KWH">{{ MILES_PER_KWH }}</option>
                          </select>
                          <span class="modal-evcharging-settings__custom-caret">&#9662;</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h2 class="section-title">Gas Savings Comparison</h2>
            <div class="form-row gds-flex-container gds-flex-container--bottom">
              <div class="form-group">
                <label>Gasoline Car MPG</label>
                <input
                type="text"
                class="form-control"
                @keyup="checkValidation()"
                v-model="evSettingsForEdit.mpgGas"
                :class="{ 'ev-charger-settings-validation-error': validationErrors.mpgGas}"/>
              </div>
              <div class="form-group">
                <label>Cost per Gallon of Gas</label>
                <input
                type="text"
                class="form-control"
                @keyup="checkValidation()"
                v-model="evSettingsForEdit.gasPricePerGallon"
                :class="{ 'ev-charger-settings-validation-error': validationErrors.gasPricePerGallon}"/>
              </div>
            </div>
            <div
              class="error-container"
              v-if="mpgEquivalentWithinRangeWarning">
              {{ mpgEquivalentWithinRangeWarning }}
            </div>
            <div
              class="error-container"
              v-if="apiErrorMessage">
              {{ apiErrorMessage }}
            </div>
            <div class="button-group gds-flex-container gds-flex-container--space-between">
              <button 
                :disabled="!formValid || isApiCallInProgress" 
                @click="updateEvSettings()" 
                class="gds-button gds-compact save-button">
                <span v-if="isApiCallInProgress">Saving...</span>
                <span v-else>Save</span>
              </button>
              <a href="#" @click="deleteEvSettings()" class="gds-button gds-compact gds-button-outline reset-link">Reset</a>
            </div>
          </div>
        </div>
      </FocusLock>
    </div>
  </div>
</template>

<script>
import GMPAPI from "../../../../services/gmpapi";
import { DumpError } from "../../../../utilities";
import {
  MPG_EQUIVALENT_MIN,
  MPG_EQUIVALENT_MAX,
  MILES_PER_KWH_MIN,
  MILES_PER_KWH_MAX,
  GAS_MPG_MIN,
  GAS_MPG_MAX,
  GAS_PRICE_MIN,
  GAS_PRICE_MAX,
  EPA_CONVERSION_FACTOR,
  MPGE,
  MILES_PER_KWH,
  CUSTOM_VEHICLE_OPTION,
  LOAD_STATE_ERROR,
  LOAD_STATE_LOADING,
  LOAD_STATE_COMPLETE
} from "../../../shared/constants";

export default {
  name: 'ModalEvChargingSettings',
  props: {
    evSettings: Object,
    accountNumber: String,
    closeModal: Function,
    onApiSuccess: Function
  },
  mounted() {
    this.initEvSettingsForEdit();
  },
  data() {
    return {
      formValid: true,
      validationErrors: {},
      evSettingsForEdit: undefined,
      loadState: LOAD_STATE_COMPLETE,
      mpgEquivalentWithinRangeWarning: undefined,
      isCustomVehicleMode: false,
      customVehicleName: '',
      CUSTOM_VEHICLE_OPTION: CUSTOM_VEHICLE_OPTION,
      MPGE: MPGE,
      MILES_PER_KWH: MILES_PER_KWH,
      isEditingEnergyConsumption: false,
      energyConsumptionValue: '',
      energyConsumptionUnit: MPGE,
      apiErrorMessage: null,
      isApiCallInProgress: false
    };
  },
  computed: {
    shouldShowEnergyConsumptionDisplay() {
      return !this.isCustomVehicleMode && 
             !this.isEditingEnergyConsumption && 
             this.evSettingsForEdit.selectedMakeModel && 
             this.evSettingsForEdit.mpgEquivalent;
    },
    shouldShowEnergyConsumptionPlaceholder() {
      return !this.isCustomVehicleMode && !this.isEditingEnergyConsumption;
    }
  },
  methods: {
    valueIsWithinRange(input, minValue, maxValue) {
      const numValue = parseFloat(input);
      return numValue >= minValue && numValue <= maxValue;
    },
    valueIsValidNumber(input) {
      if (!input) {
        return false;
      }
      
      const stringValue = input.toString().trim();
      if (stringValue === '') {
        return false;
      }
      
      const regex = /^\d+(\.\d+)?$/;
      if (!regex.test(stringValue)) {
        return false;
      }
      
      const numericValue = parseFloat(stringValue);
      if (isNaN(numericValue) || numericValue <= 0) {
        return false;
      }
      
      return true;
    },
    initEvSettingsForEdit() {
      if (this.evSettings){
          try {
            this.evSettingsForEdit = JSON.parse(JSON.stringify(this.evSettings));
            
            if (!this.evSettingsForEdit.selectedMakeModel) {
              this.evSettingsForEdit.selectedMakeModel = '';
            }
            
            // Check if the current vehicle is a custom one (not in the predefined list)
            if (this.evSettingsForEdit.selectedMakeModel) {
              const isPreDefinedModel = this.evSettingsForEdit.makeModels.some(
                model => model.name === this.evSettingsForEdit.selectedMakeModel
              );
              if (!isPreDefinedModel && this.evSettingsForEdit.selectedMakeModel !== CUSTOM_VEHICLE_OPTION) {
                // This is a custom vehicle that was previously saved
                this.isCustomVehicleMode = true;
                this.customVehicleName = this.evSettingsForEdit.selectedMakeModel;
                this.evSettingsForEdit.selectedMakeModel = CUSTOM_VEHICLE_OPTION;
                
                // Set up energy consumption for editing
                this.isEditingEnergyConsumption = true;
                if (this.evSettingsForEdit.mpgEquivalent) {
                  // Check if the stored value suggests it's in Miles/kWh format (typically < 10)
                  const mpgeValue = parseFloat(this.evSettingsForEdit.mpgEquivalent);
                  if (mpgeValue < 10) {
                    this.energyConsumptionValue = this.evSettingsForEdit.mpgEquivalent;
                    this.energyConsumptionUnit = MILES_PER_KWH;
                  } else {
                    this.energyConsumptionValue = this.evSettingsForEdit.mpgEquivalent;
                    this.energyConsumptionUnit = MPGE;
                  }
                }
              } else if (this.evSettingsForEdit.selectedMakeModel === CUSTOM_VEHICLE_OPTION) {
                this.isCustomVehicleMode = true;
                this.isEditingEnergyConsumption = true;
                if (this.evSettingsForEdit.mpgEquivalent) {
                  this.energyConsumptionValue = this.evSettingsForEdit.mpgEquivalent;
                  this.energyConsumptionUnit = MPGE;
                }
              }
            }
          } catch (error) {
            DumpError('Error in initializing ev settings', error);
          }
      }
      
      // Validate form after initialization
      this.$nextTick(() => {
        this.checkValidation();
      });
    },
    onMakeModelChange(event) {
      const selectedValue = event.target.value;
      
      if (selectedValue === CUSTOM_VEHICLE_OPTION) {
        this.isCustomVehicleMode = true;
        this.customVehicleName = '';
        this.isEditingEnergyConsumption = true;
        this.evSettingsForEdit.mpgEquivalent = '';
        this.energyConsumptionValue = '';
        this.energyConsumptionUnit = MPGE;
        this.checkValidation();
        return;
      }

      this.isCustomVehicleMode = false;
      this.isEditingEnergyConsumption = false;
      this.customVehicleName = '';

      if (selectedValue === '') {
        this.evSettingsForEdit.mpgEquivalent = '';
        this.checkValidation();
        return;
      }

      const selectedModel = this.evSettingsForEdit.makeModels.find(model => model.name === selectedValue);

      if (selectedModel) {
        this.evSettingsForEdit.mpgEquivalent = selectedModel.mpgEquivalent;
      }
      
      this.checkValidation();
    },
    startEditingEnergyConsumption() {
      this.isEditingEnergyConsumption = true;
      this.energyConsumptionValue = this.evSettingsForEdit.mpgEquivalent || '';
      this.energyConsumptionUnit = MPGE;
    },
    onEnergyConsumptionInput(event) {
      this.energyConsumptionValue = event.target.value;
      this.updateMpgEquivalent();
      this.checkValidation();
    },
    onUnitChange(event) {
      const newUnit = event.target.value;
      const currentValue = parseFloat(this.energyConsumptionValue);
      
      if (currentValue && !isNaN(currentValue)) {
        if (this.energyConsumptionUnit === MPGE && newUnit === MILES_PER_KWH) {
          // Ensure MPGe stays within bounds when converting to Miles/kWh
          const clampedMpge = Math.max(MPG_EQUIVALENT_MIN, Math.min(MPG_EQUIVALENT_MAX, currentValue));
          this.energyConsumptionValue = (clampedMpge / EPA_CONVERSION_FACTOR).toFixed(2);
        } else if (this.energyConsumptionUnit === MILES_PER_KWH && newUnit === MPGE) {
          // Clamp the resulting MPGe to stay within bounds
          const convertedMpge = currentValue * EPA_CONVERSION_FACTOR;
          const clampedMpge = Math.max(MPG_EQUIVALENT_MIN, Math.min(MPG_EQUIVALENT_MAX, convertedMpge));
          this.energyConsumptionValue = Math.round(clampedMpge).toString();
        }
      }
      
      this.energyConsumptionUnit = newUnit;
      this.updateMpgEquivalent();
      this.checkValidation();
    },
    updateMpgEquivalent() {
      if (this.energyConsumptionUnit === MILES_PER_KWH) {
        const milesPerKwh = parseFloat(this.energyConsumptionValue);
        if (milesPerKwh && !isNaN(milesPerKwh)) {
          // Clamp the converted MPGe to stay within bounds
          const convertedMpge = milesPerKwh * EPA_CONVERSION_FACTOR;
          const clampedMpge = Math.max(MPG_EQUIVALENT_MIN, Math.min(MPG_EQUIVALENT_MAX, convertedMpge));
          this.evSettingsForEdit.mpgEquivalent = Math.round(clampedMpge).toString();
        } else {
          this.evSettingsForEdit.mpgEquivalent = '';
        }
      } else {
        this.evSettingsForEdit.mpgEquivalent = this.energyConsumptionValue;
      }
    },
    async deleteEvSettings() {
      this.isApiCallInProgress = true;
      this.apiErrorMessage = null;

      try {
        await GMPAPI.deleteEvSettings(this.accountNumber);
        this.onApiSuccess();
        this.closeModal();
      } catch (err) {
        this.apiErrorMessage = 'There was a problem resetting your settings. Please try again later.';
        DumpError('Error deleting ev settings', err);
      } finally {
        this.isApiCallInProgress = false;
      }
    },
    async updateEvSettings() {
      this.isApiCallInProgress = true;
      this.apiErrorMessage = null;

      const settings = this.evSettingsForEdit;
      
      // Use custom vehicle name if in custom mode, otherwise use selected model
      const selectedMakeModel = this.isCustomVehicleMode ? this.customVehicleName : settings.selectedMakeModel;

      const postData = {
        mpgGas: settings.mpgGas,
        mpgEquivalent: settings.mpgEquivalent,
        selectedMakeModel: selectedMakeModel,
        gasPricePerGallon: settings.gasPricePerGallon
      }

      try {
        await GMPAPI.updateEvSettings(this.accountNumber, postData);
        this.onApiSuccess();
        this.closeModal();
      } catch (err) {
        this.apiErrorMessage = 'There was a problem updating your settings. Please try again later.';
        DumpError('Error updating ev settings', err);
      } finally {
        this.isApiCallInProgress = false;
      }
    },
    checkValidation() {
      const settings = this.evSettingsForEdit;

      this.validationErrors = {};
      this.mpgEquivalentWithinRangeWarning = undefined;
      this.apiErrorMessage = null;

      const mpgGas = settings.mpgGas;
      const mpgEquivalent = settings.mpgEquivalent;
      const gasPricePerGallon = settings.gasPricePerGallon;

      const mpgGasValid = this.valueIsValidNumber(mpgGas);
      const mpgEquivalentValid = this.valueIsValidNumber(mpgEquivalent);
      const gasPricePerGallonValid = this.valueIsValidNumber(gasPricePerGallon);
      
      let vehicleNameValid = true;
      if (this.isCustomVehicleMode) {
        vehicleNameValid = this.customVehicleName && this.customVehicleName.trim() !== '';
      } else {
        vehicleNameValid = settings.selectedMakeModel && settings.selectedMakeModel !== '';
      }

      if (!mpgGasValid) {
        this.validationErrors.mpgGas = true;
      }

      if (!mpgEquivalentValid) {
        this.validationErrors.mpgEquivalent = true;
      }

      if (!gasPricePerGallonValid) {
        this.validationErrors.gasPricePerGallon = true;
      }

      if (!vehicleNameValid) {
        this.validationErrors.vehicleName = true;
      }

      let mpgEquivalentWithinRange = true;

      if (mpgEquivalentValid) {
        // Check range based on current input unit when editing, or always MPGe when not editing
        if (this.isEditingEnergyConsumption || this.isCustomVehicleMode) {
          if (this.energyConsumptionUnit === MILES_PER_KWH) {
            const milesPerKwhValue = parseFloat(this.energyConsumptionValue);
            mpgEquivalentWithinRange = this.valueIsWithinRange(milesPerKwhValue, MILES_PER_KWH_MIN, MILES_PER_KWH_MAX);
            if (!mpgEquivalentWithinRange) {
              this.mpgEquivalentWithinRangeWarning = `${MILES_PER_KWH} must be between ${MILES_PER_KWH_MIN} and ${MILES_PER_KWH_MAX}`;
            }
          } else {
            const mpgeValue = parseFloat(this.energyConsumptionValue);
            mpgEquivalentWithinRange = this.valueIsWithinRange(mpgeValue, MPG_EQUIVALENT_MIN, MPG_EQUIVALENT_MAX);
            if (!mpgEquivalentWithinRange) {
              this.mpgEquivalentWithinRangeWarning = `${MPGE} must be between ${MPG_EQUIVALENT_MIN} and ${MPG_EQUIVALENT_MAX}`;
            }
          }
        } else {
          // For stored MPGe values (always in MPGe format)
          mpgEquivalentWithinRange = this.valueIsWithinRange(mpgEquivalent, MPG_EQUIVALENT_MIN, MPG_EQUIVALENT_MAX);
          if (!mpgEquivalentWithinRange) {
            this.mpgEquivalentWithinRangeWarning = `${MPGE} must be between ${MPG_EQUIVALENT_MIN} and ${MPG_EQUIVALENT_MAX}`;
          }
        }
      }

      let mpgGasWithinRange = true;
      let gasPriceWithinRange = true;

      if (mpgGasValid) {
        mpgGasWithinRange = this.valueIsWithinRange(mpgGas, GAS_MPG_MIN, GAS_MPG_MAX);
        if (!mpgGasWithinRange) {
          this.mpgEquivalentWithinRangeWarning = `Gas car MPG must be between ${GAS_MPG_MIN} and ${GAS_MPG_MAX}`;
        }
      }

      if (gasPricePerGallonValid && mpgGasWithinRange) {
        gasPriceWithinRange = this.valueIsWithinRange(gasPricePerGallon, GAS_PRICE_MIN, GAS_PRICE_MAX);
        if (!gasPriceWithinRange) {
          this.mpgEquivalentWithinRangeWarning = `Gas price must be between $${GAS_PRICE_MIN.toFixed(2)} and $${GAS_PRICE_MAX.toFixed(2)}`;
        }
      }

      this.formValid = mpgGasValid && mpgEquivalentValid && gasPricePerGallonValid && mpgEquivalentWithinRange && mpgGasWithinRange && gasPriceWithinRange && vehicleNameValid;
    }
  }
};
</script>

<style scoped></style>